import React, { useCallback, useEffect, useState } from "react";
import { api, fmtCurrency, fmtDate, formatApiError, mutateOrQueue } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Plus, ScanLine, Trash2, PackagePlus, X, Search } from "lucide-react";
import BarcodeInput from "@/components/BarcodeInput";

const EMPTY_LINE = {
  part_id: "", manufacturer: "", model: "", variant: "", year: "", part_number: "",
  name: "", bin_number: "", batch_number: "", qty: 1, purchase_price: 0, mrp: 0, gst_rate: 18, serials: "",
};

function PurchaseModal({ onClose, onSaved }) {
  const [form, setForm] = useState({
    purchase_invoice_number: "",
    purchase_date: new Date().toISOString().slice(0, 10),
    seller_name: "", seller_gstin: "", seller_address: "", seller_phone: "", note: "",
  });
  const [lines, setLines] = useState([{ ...EMPTY_LINE }]);
  const [busy, setBusy] = useState(false);

  const addLine = () => setLines([...lines, { ...EMPTY_LINE }]);
  const removeLine = (i) => setLines(lines.filter((_, idx) => idx !== i));
  const updateLine = (i, k, v) => setLines(lines.map((l, idx) => idx === i ? { ...l, [k]: v } : l));

  const lookup = async (code, lineIdx) => {
    try {
      const { data } = await api.get(`/parts/by-number/${encodeURIComponent(code)}`);
      updateLine(lineIdx, "part_id", data.id);
      updateLine(lineIdx, "part_number", data.part_number);
      updateLine(lineIdx, "manufacturer", data.manufacturer);
      updateLine(lineIdx, "model", data.model);
      updateLine(lineIdx, "variant", data.variant || "");
      updateLine(lineIdx, "year", data.year || "");
      updateLine(lineIdx, "name", data.name || "");
      updateLine(lineIdx, "bin_number", data.bin_number || "");
      updateLine(lineIdx, "purchase_price", data.purchase_price || data.cost_price || 0);
      updateLine(lineIdx, "mrp", data.mrp || data.retail_price || 0);
      updateLine(lineIdx, "gst_rate", data.gst_rate || 18);
      toast.success(`Loaded ${data.part_number}`);
    } catch (err) {
      if (err?.response?.status === 404) {
        updateLine(lineIdx, "part_number", code);
        toast.message(`No match — fill new part details`);
      } else toast.error(formatApiError(err));
    }
  };

  const submit = async () => {
    if (!form.purchase_invoice_number || !form.seller_name) return toast.error("Invoice number & seller name required");
    if (lines.length === 0) return toast.error("Add at least one line");
    setBusy(true);
    try {
      const payload = {
        ...form,
        items: lines.map((l) => ({
          part_id: l.part_id || null,
          manufacturer: l.manufacturer, model: l.model, variant: l.variant,
          year: l.year, part_number: l.part_number, name: l.name,
          bin_number: l.bin_number, batch_number: l.batch_number,
          qty: Number(l.qty), purchase_price: Number(l.purchase_price),
          mrp: Number(l.mrp), gst_rate: Number(l.gst_rate),
          serials: l.serials ? l.serials.split(/\s*,\s*|\n/).filter(Boolean) : [],
        })),
      };
      const res = await mutateOrQueue({
        method: "post",
        url: "/purchases",
        body: payload,
        label: `Purchase · ${form.purchase_invoice_number} · ${form.seller_name}`,
      });
      if (res.queued) toast.success("Offline — purchase queued and will sync when you reconnect");
      else toast.success(`Purchase ${res.data.purchase_invoice_number} recorded · ${res.data.items.length} line(s) added to stock`);
      onSaved();
    } catch (err) { toast.error(formatApiError(err)); } finally { setBusy(false); }
  };

  const totals = lines.reduce((acc, l) => {
    const sub = Number(l.purchase_price) * Number(l.qty);
    const gst = sub * Number(l.gst_rate || 0) / 100;
    acc.sub += sub; acc.gst += gst; acc.grand += sub + gst;
    return acc;
  }, { sub: 0, gst: 0, grand: 0 });

  return (
    <div className="fixed inset-0 bg-zinc-950/40 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white border border-zinc-300 w-full max-w-6xl max-h-[95vh] flex flex-col" onClick={(e) => e.stopPropagation()} data-testid="purchase-modal">
        <div className="px-6 py-4 border-b border-zinc-200 flex items-center justify-between">
          <div>
            <div className="label-eyebrow">New purchase</div>
            <h3 className="text-xl font-semibold text-zinc-950">Enter Purchase Invoice</h3>
          </div>
          <button onClick={onClose} className="p-1 hover:bg-zinc-100"><X className="w-4 h-4" /></button>
        </div>

        <div className="flex-1 overflow-y-auto p-6 space-y-6">
          {/* Header */}
          <div className="grid grid-cols-4 gap-4">
            {[
              ["purchase_invoice_number", "Invoice Number *"],
              ["purchase_date", "Date *", "date"],
              ["seller_name", "Seller Name *"],
              ["seller_gstin", "Seller GSTIN"],
              ["seller_phone", "Seller Phone"],
              ["seller_address", "Seller Address"],
              ["note", "Note"],
            ].map(([k, lbl, type = "text"]) => (
              <div key={k} className={k === "seller_address" || k === "note" ? "col-span-2" : ""}>
                <label className="label-eyebrow block mb-2">{lbl}</label>
                <input
                  data-testid={`purchase-${k}`}
                  type={type}
                  value={form[k]}
                  onChange={(e) => setForm({ ...form, [k]: e.target.value })}
                  className="w-full bg-white border border-zinc-300 text-sm px-3 py-2 font-mono focus:outline-none focus:ring-2 focus:ring-[#002FA7]"
                />
              </div>
            ))}
          </div>

          {/* Lines */}
          <div className="border border-zinc-300">
            <div className="px-4 py-3 border-b border-zinc-200 bg-zinc-50 flex items-center justify-between">
              <div className="label-eyebrow">Line items</div>
              <button data-testid="purchase-add-line" onClick={addLine} className="text-xs uppercase tracking-wider px-3 py-1.5 bg-zinc-950 text-white inline-flex items-center gap-1 hover:bg-zinc-800">
                <Plus className="w-3 h-3" /> Add line
              </button>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="bg-zinc-50 text-zinc-500 uppercase tracking-wider text-[10px] border-b border-zinc-200">
                    <th className="text-left p-2">Scan or Part #</th>
                    <th className="text-left p-2">Mfg / Model / Variant</th>
                    <th className="text-left p-2">Name</th>
                    <th className="text-left p-2">Batch</th>
                    <th className="text-left p-2">Bin</th>
                    <th className="text-right p-2">Qty</th>
                    <th className="text-right p-2">Purchase ₹</th>
                    <th className="text-right p-2">MRP ₹</th>
                    <th className="text-right p-2">GST%</th>
                    <th className="text-left p-2">Serials (csv)</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {lines.map((l, i) => (
                    <tr key={`pline-${i}-${l.part_number || "new"}`} className="border-b border-zinc-100 align-top">
                      <td className="p-1 space-y-1">
                        <input
                          data-testid={`purchase-line-${i}-part_number`}
                          value={l.part_number}
                          onChange={(e) => updateLine(i, "part_number", e.target.value)}
                          placeholder="Part # *"
                          className="w-44 border border-zinc-300 px-2 py-1 font-mono text-xs"
                        />
                        <BarcodeInput
                          testid={`purchase-line-${i}-barcode`}
                          placeholder="…or scan"
                          onScan={(code) => lookup(code, i)}
                        />
                      </td>
                      <td className="p-1 space-y-1">
                        <input data-testid={`purchase-line-${i}-manufacturer`} value={l.manufacturer} onChange={(e) => updateLine(i, "manufacturer", e.target.value)} placeholder="Mfg *" className="w-full border border-zinc-300 px-2 py-1 font-mono text-xs" />
                        <input data-testid={`purchase-line-${i}-model`} value={l.model} onChange={(e) => updateLine(i, "model", e.target.value)} placeholder="Model *" className="w-full border border-zinc-300 px-2 py-1 font-mono text-xs" />
                        <input data-testid={`purchase-line-${i}-variant`} value={l.variant} onChange={(e) => updateLine(i, "variant", e.target.value)} placeholder="Variant" className="w-full border border-zinc-300 px-2 py-1 font-mono text-xs" />
                      </td>
                      <td className="p-1"><input data-testid={`purchase-line-${i}-name`} value={l.name} onChange={(e) => updateLine(i, "name", e.target.value)} className="w-full border border-zinc-300 px-2 py-1 font-mono text-xs" /></td>
                      <td className="p-1"><input data-testid={`purchase-line-${i}-batch`} value={l.batch_number} onChange={(e) => updateLine(i, "batch_number", e.target.value)} className="w-full border border-zinc-300 px-2 py-1 font-mono text-xs" /></td>
                      <td className="p-1"><input data-testid={`purchase-line-${i}-bin`} value={l.bin_number} onChange={(e) => updateLine(i, "bin_number", e.target.value)} className="w-full border border-zinc-300 px-2 py-1 font-mono text-xs" /></td>
                      <td className="p-1"><input data-testid={`purchase-line-${i}-qty`} type="number" min="1" value={l.qty} onChange={(e) => updateLine(i, "qty", e.target.value)} className="w-20 border border-zinc-300 px-2 py-1 font-mono text-xs text-right" /></td>
                      <td className="p-1"><input data-testid={`purchase-line-${i}-purchase_price`} type="number" step="0.01" value={l.purchase_price} onChange={(e) => updateLine(i, "purchase_price", e.target.value)} className="w-24 border border-zinc-300 px-2 py-1 font-mono text-xs text-right" /></td>
                      <td className="p-1"><input data-testid={`purchase-line-${i}-mrp`} type="number" step="0.01" value={l.mrp} onChange={(e) => updateLine(i, "mrp", e.target.value)} className="w-24 border border-zinc-300 px-2 py-1 font-mono text-xs text-right" /></td>
                      <td className="p-1"><input data-testid={`purchase-line-${i}-gst_rate`} type="number" step="0.01" value={l.gst_rate} onChange={(e) => updateLine(i, "gst_rate", e.target.value)} className="w-16 border border-zinc-300 px-2 py-1 font-mono text-xs text-right" /></td>
                      <td className="p-1"><textarea data-testid={`purchase-line-${i}-serials`} rows={2} placeholder="auto if blank" value={l.serials} onChange={(e) => updateLine(i, "serials", e.target.value)} className="w-44 border border-zinc-300 px-2 py-1 font-mono text-[10px]" /></td>
                      <td className="p-1"><button onClick={() => removeLine(i)} className="text-rose-600 p-1"><Trash2 className="w-3 h-3" /></button></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Totals */}
          <div className="flex justify-end">
            <div className="w-72 space-y-1 text-sm">
              <div className="flex justify-between"><span className="text-zinc-500">Subtotal</span><span className="font-mono">{fmtCurrency(totals.sub)}</span></div>
              <div className="flex justify-between"><span className="text-zinc-500">GST</span><span className="font-mono">{fmtCurrency(totals.gst)}</span></div>
              <div className="flex justify-between border-t border-zinc-300 pt-2 font-bold"><span>Grand Total</span><span className="font-mono text-lg" data-testid="purchase-grand-total">{fmtCurrency(totals.grand)}</span></div>
            </div>
          </div>
        </div>

        <div className="px-6 py-4 border-t border-zinc-200 flex justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 border border-zinc-300 text-sm uppercase tracking-wider hover:bg-zinc-50">Cancel</button>
          <button data-testid="purchase-submit-button" disabled={busy} onClick={submit} className="px-4 py-2 bg-zinc-950 text-white text-sm uppercase tracking-wider hover:bg-zinc-800 disabled:opacity-50">{busy ? "Saving..." : "Save Purchase"}</button>
        </div>
      </div>
    </div>
  );
}

export default function Purchases() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const canCreate = ["admin", "inventory_manager"].includes(user.role);

  const load = useCallback(() => api.get("/purchases").then(({ data }) => setItems(data)), []);
  useEffect(() => { load(); }, [load]);

  return (
    <div className="space-y-6" data-testid="purchases-root">
      <div className="flex items-end justify-between border-b border-zinc-200 pb-6">
        <div>
          <div className="label-eyebrow">Inwards</div>
          <h1 className="text-4xl font-bold tracking-tight text-zinc-950">Purchase Entries</h1>
          <p className="text-sm text-zinc-500 mt-2">Record purchases from suppliers. Each unit gets a unique serial number for tracking.</p>
        </div>
        {canCreate && (
          <button data-testid="new-purchase-button" onClick={() => setOpen(true)} className="bg-zinc-950 text-white text-sm uppercase tracking-wider font-semibold px-4 py-2.5 hover:bg-zinc-800 flex items-center gap-2">
            <Plus className="w-4 h-4" /> New purchase
          </button>
        )}
      </div>

      <div className="brutalist-card overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-zinc-50 text-zinc-500 uppercase tracking-wider text-[11px] border-b border-zinc-200">
              <th className="text-left px-5 py-3 font-semibold">Inv #</th>
              <th className="text-left px-5 py-3 font-semibold">Date</th>
              <th className="text-left px-5 py-3 font-semibold">Seller</th>
              <th className="text-left px-5 py-3 font-semibold">GSTIN</th>
              <th className="text-right px-5 py-3 font-semibold">Lines</th>
              <th className="text-right px-5 py-3 font-semibold">GST</th>
              <th className="text-right px-5 py-3 font-semibold">Total</th>
              <th className="text-left px-5 py-3 font-semibold">Recorded by</th>
            </tr>
          </thead>
          <tbody>
            {items.length === 0 && <tr><td colSpan={8} className="px-5 py-12 text-center text-sm text-zinc-500"><PackagePlus className="w-6 h-6 mx-auto mb-2 text-zinc-300" />No purchase entries yet.</td></tr>}
            {items.map((p) => (
              <tr key={p.id} className="border-t border-zinc-200 hover:bg-zinc-50" data-testid={`purchase-row-${p.purchase_invoice_number}`}>
                <td className="px-5 py-3 font-mono text-zinc-950 font-medium">{p.purchase_invoice_number}</td>
                <td className="px-5 py-3 font-mono text-xs text-zinc-500">{p.purchase_date}</td>
                <td className="px-5 py-3 text-zinc-700">{p.seller_name}</td>
                <td className="px-5 py-3 font-mono text-xs text-zinc-500">{p.seller_gstin || "—"}</td>
                <td className="px-5 py-3 text-right font-mono">{p.items.length}</td>
                <td className="px-5 py-3 text-right font-mono">{fmtCurrency(p.gst_total)}</td>
                <td className="px-5 py-3 text-right font-mono font-semibold">{fmtCurrency(p.grand_total)}</td>
                <td className="px-5 py-3 text-xs text-zinc-500">{p.created_by_name}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {open && <PurchaseModal onClose={() => setOpen(false)} onSaved={() => { setOpen(false); load(); }} />}
    </div>
  );
}
